<!-- RiderProfile.vue -->
<template>
	<App title="Rider's Profile">
		<div class="rider-layout">
            <!-- ── Outstanding Documentation Banner ─────────────────────────── -->
            <!-- ── Outstanding Documentation Banner ─────────────────────────── -->
            <div v-if="!rider.agreement || !rider.indemnity" class="d-flex align-items-center gap-3 px-4 py-3 border border-warning-subtle bg-warning-subtle rounded-3">
                <i class="fas fa-triangle-exclamation text-warning fs-5"></i>
                <div class="d-flex align-items-center gap-2 flex-wrap">
                    <span class="text-warning-emphasis fw-semibold small">Outstanding Documentation —</span>
                    <span class="text-warning-emphasis small">The following documents have not been submitted:</span>
                    <span v-if="!rider.agreement" class="badge text-bg-warning fw-semibold">Agreement</span>
                    <span v-if="!rider.indemnity" class="badge text-bg-warning fw-semibold">Indemnity Form</span>
                </div>
            </div>

			<!-- ── Hero card ──────────────────────────────────────────── -->
			<div class="rider-hero">
				<div class="rider-hero-avatar-wrap">
					<div class="rider-hero-avatar">
						<img v-if="rider?.avatar?.url" :src="rider?.avatar?.url" alt="Avatar" class="w-100 h-100 object-fit-cover" />
						<span v-else>{{ (rider?.firstname?.[0] ?? '').toUpperCase() }}{{ (rider?.lastname?.[0] ?? '').toUpperCase() }}</span>
					</div>
					<span class="rider-status-dot" :class="rider.has_active_engine ? 'active' : 'inactive'"></span>
				</div>

				<div class="rider-hero-info">
					<h2 class="rider-hero-name">{{ rider?.firstname }} {{ rider?.lastname }}</h2>
					<span class="rider-role-badge"><i class="fas fa-motorcycle me-1"></i> Rider</span>
					<div class="rider-hero-contacts">
						<span><i class="fas fa-envelope me-1 text-muted"></i>{{ rider?.email }}</span>
						<span><i class="fas fa-phone me-1 text-muted"></i>{{ rider?.phone }}</span>
						<span v-if="rider?.address?.street"><i class="fas fa-map-marker-alt me-1 text-muted"></i>{{ rider?.address?.street }}, {{ rider?.address?.state }}, {{ rider?.address?.country }}</span>
					</div>
				</div>

				<div class="rider-hero-actions">
					<Link v-if="rider && rider.id" :href="route('rider.page.update', rider.id)" as="button" class="btn btn-sm btn-outline-secondary">
						<i class="fas fa-edit me-1"></i> Edit
					</Link>
					<button v-if="rider.has_active_engine" type="button" class="btn btn-sm btn-outline-danger" @click="confirmAction">
						<i class="fas fa-times me-1"></i> Deactivate
					</button>
				</div>
			</div>

			<!-- Stats row -->
			<div class="rider-stats">
				<div class="rider-stat">
					<div class="rider-stat-icon"><i class="fas fa-wallet"></i></div>
					<div>
						<div class="rider-stat-label">Income</div>
						<div class="rider-stat-value">{{ rider?.income?.formatted || 'N/A' }}</div>
					</div>
				</div>
				<div class="rider-stat">
					<div class="rider-stat-icon"><i class="fas fa-cog"></i></div>
					<div>
						<div class="rider-stat-label">Engine</div>
						<div class="rider-stat-value">{{ rider?.current_engine?.data[0]?.serial || 'Unassigned' }}</div>
					</div>
				</div>
				<div class="rider-stat">
					<div class="rider-stat-icon"><i class="fas fa-id-card"></i></div>
					<div>
						<div class="rider-stat-label">BVN</div>
						<div class="rider-stat-value">{{ rider.bvn || 'N/A' }}</div>
					</div>
				</div>
				<div class="rider-stat">
					<div class="rider-stat-icon"><i class="fas fa-circle" :class="rider.has_active_engine ? 'text-success' : 'text-danger'"></i></div>
					<div>
						<div class="rider-stat-label">Status</div>
						<div class="rider-stat-value" :class="rider.has_active_engine ? 'text-success' : 'text-danger'">
							{{ rider.has_active_engine ? 'Active' : 'Inactive' }}
						</div>
					</div>
				</div>
			</div>

			<!-- ── Assign Engine ────────────────────────────────────────── -->
			<div v-if=" !rider.has_active_engine" class="rider-section">
				<div class="rider-section-header">
					<div>
						<div class="rider-section-title"><i class="fas fa-cog me-2 text-muted"></i>Assign Engine</div>
						<div class="rider-section-desc">Select an engine to activate this rider</div>
					</div>
					<button type="button" class="rider-toggle-btn" @click="toggleEngineSection">
						<i class="fas" :class="showEngineSection ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
						{{ showEngineSection ? 'Hide' : 'Show' }}
					</button>
				</div>
				<div v-if="showEngineSection" class="rider-section-body">
					<Form ref="form" @submit="onSubmit">
						<div class="d-flex align-items-end gap-3">
							<div class="flex-grow-1">
								<SelectField id="engine_activation" v-model="engine_activation" name="engine_activation" rules="required" label="Select Engine" :options="type_options" />
								<small class="text-muted"><i class="fas fa-info-circle me-1"></i>Choose an available engine to activate this rider's account</small>
							</div>
						</div>
						<div class="rider-section-footer">
							<button type="button" class="btn btn-sm btn-outline-danger" @click="cancelEngineSection">
								<i class="fas fa-times me-1"></i> Cancel
							</button>
							<Button id="action" type="submit" :label="label" :loading="loading" :done="done" class="btn btn-sm btn-primary" :disabled="!engine_activation" />
						</div>
					</Form>
				</div>
			</div>

			<!-- ── Documents ────────────────────────────────────────────── -->
			<div class="rider-section">
				<div class="rider-section-header">
					<div>
						<div class="rider-section-title"><i class="fas fa-file-alt me-2 text-muted"></i>Documents</div>
						<div class="rider-section-desc">Uploaded identification and credentials</div>
					</div>
					<button type="button" class="rider-toggle-btn" @click="toggleDocumentsSection">
						<i class="fas" :class="showDocumentsSection ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
						{{ showDocumentsSection ? 'Hide' : 'Show' }}
					</button>
				</div>
				<div v-if="showDocumentsSection" class="rider-section-body">
					<div class="rider-docs-grid">
						<div v-if="rider?.identification" class="rider-doc-card" @click="openDocPreview(rider.identification.url, 'Government ID', rider.identification.type)">
							<div class="rider-doc-img">
								<img :src="rider?.identification?.url" class="img-fluid w-100 h-100 object-fit-cover" />
								<div class="rider-doc-overlay"><i class="fas fa-search-plus"></i></div>
							</div>
							<div class="rider-doc-label">Government ID</div>
						</div>
						<div v-if="rider?.ownership" class="rider-doc-card" @click="openDocPreview(rider.ownership.url, 'Ownership Document', rider.ownership.type)">
							<div class="rider-doc-img">
								<img :src="rider.ownership.url" class="img-fluid w-100 h-100 object-fit-cover" />
								<div class="rider-doc-overlay"><i class="fas fa-search-plus"></i></div>
							</div>
							<div class="rider-doc-label">Ownership Document</div>
						</div>
						<div v-if="rider?.avatar" class="rider-doc-card" @click="openDocPreview(rider.avatar.url, 'Rider Passport', rider.avatar.type)">
							<div class="rider-doc-img">
								<img :src="rider?.avatar?.url" class="img-fluid w-100 h-100 object-fit-cover" />
								<div class="rider-doc-overlay"><i class="fas fa-search-plus"></i></div>
							</div>
							<div class="rider-doc-label">Rider Passport</div>
						</div>
						<div v-if="rider.signature" class="rider-doc-card" @click="openDocPreview(rider.signature.url, 'Signature', rider.signature.type)">
							<div class="rider-doc-img">
								<img :src="rider?.signature?.url" class="img-fluid w-100 h-100 object-fit-cover" />
								<div class="rider-doc-overlay"><i class="fas fa-search-plus"></i></div>
							</div>
							<div class="rider-doc-label">Signature</div>
						</div>
					</div>
				</div>
			</div>
           
            <!-- ── Legal Documents ──────────────────────────────────────────── -->
            <div class="rider-section">
                <div class="rider-section-header">
                    <div>
                        <div class="rider-section-title"><i class="fas fa-file-contract me-2 text-muted"></i>Legal Documents</div>
                        <div class="rider-section-desc">Signed agreements and legal declarations</div>
                    </div>
                    <button type="button" class="rider-toggle-btn" @click="toggleLegalSection">
                        <i class="fas" :class="showLegalSection ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
                        {{ showLegalSection ? 'Hide' : 'Show' }}
                    </button>
                </div>
                <div v-if="showLegalSection" class="rider-section-body rider-legal-docs-body">
                    <div class="row g-3">

                        <!-- Agreement -->
                        <div class="col-12">
                            <div v-if="rider?.agreement" class="rider-legal-doc-card">
                                <div class="rider-legal-doc-viewer">
                                    <img
                                        v-if="isImageDocument(rider.agreement.type, rider.agreement.url)"
                                        :src="rider.agreement.url"
                                        alt="Agreement"
                                        class="rider-legal-doc-img"
                                    />
                                    <iframe
                                        v-else
                                        :src="documentViewerUrl(rider.agreement.url, rider.agreement.type)"
                                        title="Agreement"
                                        class="rider-legal-doc-frame"
                                    ></iframe>
                                    <button
                                        type="button"
                                        class="rider-legal-doc-open"
                                        aria-label="Preview agreement"
                                        @click="openDocPreview(rider.agreement.url, 'Agreement', rider.agreement.type)"
                                    >
                                        <i class="fas fa-search-plus fs-4"></i>
                                    </button>
                                </div>
                                <div class="rider-legal-doc-footer">
                                    <span class="small fw-semibold text-secondary">Agreement</span>
                                </div>
                            </div>
                            <div v-else class="border border-dashed rounded-3 overflow-hidden bg-light">
                                <div class="d-flex align-items-center justify-content-center" style="height:160px;">
                                    <i class="fas fa-file-circle-xmark text-secondary fs-2"></i>
                                </div>
                                <div class="px-3 py-2 bg-white border-top">
                                    <span class="small fw-semibold text-secondary">Agreement — Not Uploaded</span>
                                </div>
                            </div>
                        </div>

                        <!-- Indemnity Form -->
                        <div class="col-12">
                            <div v-if="rider?.indemnity" class="rider-legal-doc-card">
                                <div class="rider-legal-doc-viewer">
                                    <img
                                        v-if="isImageDocument(rider.indemnity.type, rider.indemnity.url)"
                                        :src="rider.indemnity.url"
                                        alt="Indemnity Form"
                                        class="rider-legal-doc-img"
                                    />
                                    <iframe
                                        v-else
                                        :src="documentViewerUrl(rider.indemnity.url, rider.indemnity.type)"
                                        title="Indemnity Form"
                                        class="rider-legal-doc-frame"
                                    ></iframe>
                                    <button
                                        type="button"
                                        class="rider-legal-doc-open"
                                        aria-label="Preview indemnity form"
                                        @click="openDocPreview(rider.indemnity.url, 'Indemnity Form', rider.indemnity.type)"
                                    >
                                        <i class="fas fa-search-plus fs-4"></i>
                                    </button>
                                </div>
                                <div class="rider-legal-doc-footer">
                                    <span class="small fw-semibold text-secondary">Indemnity Form</span>
                                </div>
                            </div>
                            <div v-else class="border border-dashed rounded-3 overflow-hidden bg-light">
                                <div class="d-flex align-items-center justify-content-center" style="height:160px;">
                                    <i class="fas fa-file-circle-xmark text-secondary fs-2"></i>
                                </div>
                                <div class="px-3 py-2 bg-white border-top">
                                    <span class="small fw-semibold text-secondary">Indemnity Form — Not Uploaded</span>
                                </div>
                            </div>
                        </div>


                    </div>
                </div>
            </div>

			<!-- ── Create Credit Plan ────────────────────────────────────── -->
			<div class="rider-section">
				<div class="rider-section-header">
					<div>
						<div class="rider-section-title"><i class="fas fa-plus-circle me-2 text-muted"></i>Create Credit Plan</div>
						<div class="rider-section-desc">Set up a new repayment schedule for offline credit</div>
					</div>
					<button type="button" class="rider-toggle-btn" @click="toggleCreditFormSection">
						<i class="fas" :class="showCreditFormSection ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
						{{ showCreditFormSection ? 'Hide' : 'Show' }}
					</button>
				</div>
				<div v-if="showCreditFormSection" class="rider-section-body">
					<CreditForm :url="route('credit.ajax.create')" :credit-types="credits" :rider-id="rider.id" :has-credit-agreement="!!rider?.credit_agreement" @success="handleCreditSuccess" @error="handleCreditError" @cancel="cancelCreditForm" />
				</div>
			</div>

			<!-- ── Active Credit Plans & History ─────────────────────────── -->
			<div class="rider-section">
				<div class="rider-section-header">
					<div>
						<div class="rider-section-title"><i class="fas fa-history me-2 text-muted"></i>Active Credit Plans &amp; History</div>
						<div class="rider-section-desc">Manage credit and repair repayment schedules</div>
					</div>
					<button type="button" class="rider-toggle-btn" @click="toggleActivePlansSection">
						<i class="fas" :class="showActivePlansSection ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
						{{ showActivePlansSection ? 'Hide' : 'Show' }}
					</button>
				</div>
				<div v-if="showActivePlansSection" class="rider-section-body">
					<ul class="rider-tabs nav nav-tabs mb-4" role="tablist">
						<li class="nav-item" role="presentation">
							<button class="nav-link active" id="credit-tab" data-bs-toggle="tab" data-bs-target="#credit-panel" type="button" role="tab">
								<i class="fas fa-circle text-success me-1" style="font-size:0.55rem;vertical-align:middle;"></i> Active Credit Plans
							</button>
						</li>
						<li class="nav-item" role="presentation">
							<button class="nav-link" id="credithistory-tab" data-bs-toggle="tab" data-bs-target="#credithistory-panel" type="button" role="tab">
								Normal Credit History
							</button>
						</li>
						<li class="nav-item" role="presentation">
							<button class="nav-link" id="credit-repair-history-tab" data-bs-toggle="tab" data-bs-target="#credit-repair-history-panel" type="button" role="tab">
								Repair Credit History
							</button>
						</li>
					</ul>
					<div class="tab-content">
						<div class="tab-pane fade show active" id="credit-panel" role="tabpanel">
							<TableComponent :data="rider.active_credit_entities" :columns="creditColumns" title="Active Credit Plans" subtitle="Credit Plans currently repaid by this rider" date-column="created_at" />
						</div>
						<div class="tab-pane fade" id="credithistory-panel" role="tabpanel">
							<Datatable :options="rider_credit_options" />
						</div>
						<div class="tab-pane fade" id="credit-repair-history-panel" role="tabpanel">
							<Datatable :options="rider_credit_repair_options" />
						</div>
					</div>
				</div>
			</div>

			<!-- ── Swap & Save ───────────────────────────────────────────── -->
			<div class="rider-section">
				<div class="rider-section-header">
					<div>
						<div class="rider-section-title"><i class="fas fa-exchange-alt me-2 text-muted"></i>Swap &amp; Save</div>
						<div class="rider-section-desc">Historical data and performance metrics</div>
					</div>
					<button type="button" class="rider-toggle-btn" @click="toggleSwapSection">
						<i class="fas" :class="showSwapSection ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
						{{ showSwapSection ? 'Hide' : 'Show' }}
					</button>
				</div>
				<div v-if="showSwapSection" class="rider-section-body">
					<SwapsaveTable :rider="rider" />
				</div>
			</div>

			<!-- ── Performance Analytics ─────────────────────────────────── -->
			<div class="rider-section">
				<div class="rider-section-header">
					<div>
						<div class="rider-section-title"><i class="fas fa-chart-bar me-2 text-muted"></i>Performance Analytics</div>
						<div class="rider-section-desc">Historical data and performance metrics</div>
					</div>
					<button type="button" class="rider-toggle-btn" @click="toggleAnalyticsSection">
						<i class="fas" :class="showAnalyticsSection ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
						{{ showAnalyticsSection ? 'Hide' : 'Show' }}
					</button>
				</div>
				<div v-if="showAnalyticsSection" class="rider-section-body">
					<EnhancedTableComponent ref="ridersTable" :api-endpoint="route('rider.ajax.history', { rider: rider.id })" :columns="recordColumns" title="Swap History" subtitle="Rider's Swap History" :enable-search="true" :enable-date-filter="true" :has-actions="false" row-key="id" :default-items-per-page="25" />
				</div>
			</div>

			<!-- Alert -->
			<div v-if="message" class="alert alert-info border-0 shadow-sm rounded-3">
				<i class="fas fa-info-circle me-2"></i>{{ message }}
			</div>

		</div>
	</App>

	<!-- Notifications -->
	<NotivueKeyboard v-slot="{ containersTabIndex }">
		<Notivue v-slot="item" :containersTabIndex="containersTabIndex">
			<AppNotification :item="item" />
		</Notivue>
	</NotivueKeyboard>

	<!-- Document Preview Modal -->
	<Teleport to="body">
		<div v-if="docPreview.open" class="rider-doc-lightbox" @click.self="closeDocPreview">
			<div class="rider-doc-lightbox-inner">
				<div class="rider-doc-lightbox-header">
					<span class="rider-doc-lightbox-title">{{ docPreview.label }}</span>
					<button type="button" class="rider-doc-lightbox-close" @click="closeDocPreview">
						<i class="fas fa-times"></i>
					</button>
				</div>
				<div class="rider-doc-lightbox-body">
					<img v-if="isImageDocument(docPreview.type, docPreview.url)" :src="docPreview.url" :alt="docPreview.label" class="rider-doc-lightbox-img" />
					<iframe v-else :src="documentViewerUrl(docPreview.url, docPreview.type)" :title="docPreview.label" class="rider-doc-lightbox-frame"></iframe>
				</div>
			</div>
		</div>
	</Teleport>

</template>

<style scoped lang="scss">
// ─── Layout ───────────────────────────────────────────────────────────────────
.rider-layout {
	margin: 0 auto;
	display: flex;
	flex-direction: column;
	gap: 1.25rem;
}

// ─── Hero ─────────────────────────────────────────────────────────────────────
.rider-hero {
	display: flex;
	align-items: flex-start;
	gap: 1.5rem;
	background: #fff;
	border-radius: 12px;
	padding: 1.75rem;
	border: 1px solid #e9ecef;
	box-shadow: 0 1px 4px rgba(0,0,0,0.06);
	flex-wrap: wrap;
}

.rider-hero-avatar-wrap {
	flex-shrink: 0;
	position: relative;
	width: 88px;
	height: 88px;
}

.rider-hero-avatar {
	width: 88px;
	height: 88px;
	border-radius: 50%;
	background: linear-gradient(135deg, #1e3a2f, #3a7d52);
	color: #fff;
	font-size: 1.75rem;
	font-weight: 700;
	display: flex;
	align-items: center;
	justify-content: center;
	overflow: hidden;
	border: 3px solid #fff;
	box-shadow: 0 4px 14px rgba(30,58,47,0.25);
}

.rider-status-dot {
	position: absolute;
	bottom: 10px;
	right: 2px;
	width: 14px;
	height: 14px;
	border-radius: 50%;
	border: 2px solid #fff;

	&.active  { background: #22c55e; }
	&.inactive { background: #ef4444; }
}

.rider-hero-info {
	flex: 1;
	min-width: 0;
}

.rider-hero-name {
	font-size: 1.3rem;
	font-weight: 700;
	color: #111827;
	margin-bottom: 0.3rem;
	line-height: 1.2;
}

.rider-role-badge {
	display: inline-block;
	padding: 0.2rem 0.75rem;
	border-radius: 999px;
	font-size: 0.72rem;
	font-weight: 600;
	text-transform: uppercase;
	letter-spacing: 0.06em;
	background: #dcfce7;
	color: #15803d;
	margin-bottom: 0.6rem;
}

.rider-hero-contacts {
	display: flex;
	flex-direction: column;
	gap: 0.25rem;
	font-size: 0.82rem;
	color: #374151;
}

.rider-hero-actions {
	flex-shrink: 0;
	display: flex;
	gap: 0.5rem;
	align-items: flex-start;
	margin-left: auto;
}

// ─── Stats ────────────────────────────────────────────────────────────────────
.rider-stats {
	display: grid;
	grid-template-columns: repeat(4, 1fr);
	gap: 0.875rem;

	@media (max-width: 640px) { grid-template-columns: repeat(2, 1fr); }
}

.rider-stat {
	display: flex;
	align-items: center;
	gap: 0.75rem;
	background: #fff;
	border-radius: 10px;
	padding: 0.875rem 1rem;
	border: 1px solid #e9ecef;
	box-shadow: 0 1px 3px rgba(0,0,0,0.04);
}

.rider-stat-icon {
	flex-shrink: 0;
	width: 36px;
	height: 36px;
	border-radius: 8px;
	background: #f0fdf4;
	color: #16a34a;
	display: flex;
	align-items: center;
	justify-content: center;
	font-size: 0.85rem;
}

.rider-stat-label {
	font-size: 0.68rem;
	font-weight: 600;
	text-transform: uppercase;
	letter-spacing: 0.05em;
	color: #9ca3af;
	margin-bottom: 0.1rem;
}

.rider-stat-value {
	font-size: 0.85rem;
	font-weight: 600;
	color: #111827;
}

// ─── Section cards ────────────────────────────────────────────────────────────
.rider-section {
	background: #fff;
	border-radius: 12px;
	border: 1px solid #e9ecef;
	box-shadow: 0 1px 4px rgba(0,0,0,0.06);
	overflow: hidden;
}

.rider-section-header {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 1rem;
	padding: 1rem 1.5rem;
	background: #f9fafb;
	border-bottom: 1px solid #f1f3f5;
}

.rider-section-title {
	font-size: 0.9rem;
	font-weight: 700;
	color: #111827;
	margin-bottom: 0.2rem;
}

.rider-section-desc {
	font-size: 0.75rem;
	color: #6b7280;
}

.rider-section-body {
	padding: 1.5rem;
}

.rider-section-footer {
	display: flex;
	justify-content: flex-end;
	gap: 0.5rem;
	margin-top: 1.25rem;
	padding-top: 1rem;
	border-top: 1px solid #f1f3f5;
}

.rider-toggle-btn {
	flex-shrink: 0;
	display: inline-flex;
	align-items: center;
	gap: 0.35rem;
	font-size: 0.75rem;
	font-weight: 600;
	padding: 0.35rem 0.75rem;
	border-radius: 6px;
	border: 1px solid #e5e7eb;
	background: #fff;
	color: #374151;
	cursor: pointer;
	transition: background 0.15s, border-color 0.15s;

	&:hover {
		background: #f3f4f6;
		border-color: #d1d5db;
	}
}

// ─── Documents ────────────────────────────────────────────────────────────────
.rider-docs-grid {
	display: grid;
	grid-template-columns: repeat(4, 1fr);
	gap: 1rem;

	@media (max-width: 768px) { grid-template-columns: repeat(2, 1fr); }
}

.rider-doc-card {
	border-radius: 10px;
	overflow: hidden;
	border: 1px solid #e9ecef;
	background: #f9fafb;
}

.rider-doc-img {
	height: 140px;
	overflow: hidden;
	background: #e5e7eb;
}

.rider-doc-label {
	font-size: 0.75rem;
	font-weight: 600;
	color: #6b7280;
	text-align: center;
	padding: 0.5rem;
}

.rider-legal-docs-body {
    max-height: 560px;
    overflow-y: auto;
    overflow-x: hidden;
}

.rider-legal-doc-card {
    border-radius: 10px;
    overflow: hidden;
    border: 1px solid #e9ecef;
    background: #f9fafb;
}

.rider-legal-doc-viewer {
    position: relative;
    height: 360px;
    background: #f3f4f6;
}

.rider-legal-doc-img,
.rider-legal-doc-frame {
    width: 100%;
    height: 100%;
    display: block;
    background: #fff;
}

.rider-legal-doc-img {
    object-fit: contain;
}

.rider-legal-doc-frame {
    border: 0;
}

.rider-legal-doc-open {
    position: absolute;
    top: 0.75rem;
    right: 0.75rem;
    z-index: 1;
    width: 2.4rem;
    height: 2.4rem;
    border: 1px solid rgba(255, 255, 255, 0.45);
    border-radius: 8px;
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    background: rgba(17, 24, 39, 0.72);
    cursor: pointer;
    box-shadow: 0 8px 22px rgba(0, 0, 0, 0.22);
    transition: background 0.15s, transform 0.15s;

    &:hover {
        background: rgba(17, 24, 39, 0.9);
        transform: translateY(-1px);
    }
}

.rider-legal-doc-footer {
    padding: 0.625rem 1rem;
    background: #fff;
    border-top: 1px solid #e9ecef;
}

// ─── Tabs ─────────────────────────────────────────────────────────────────────
.rider-tabs.nav-tabs {
	border-bottom: 2px solid #f1f3f5;
	gap: 0;

	.nav-link {
		font-size: 0.8rem;
		font-weight: 500;
		color: #6b7280;
		border: none;
		border-bottom: 2px solid transparent;
		margin-bottom: -2px;
		padding: 0.5rem 1rem;
		transition: color 0.15s;

		&:hover { color: #374151; }

		&.active {
			color: #16a34a;
			border-bottom-color: #16a34a;
			background: transparent;
			font-weight: 600;
		}
	}
}

.tab-content {
	animation: fadeIn 0.25s ease;
}

@keyframes fadeIn {
	from { opacity: 0; transform: translateY(4px); }
	to   { opacity: 1; transform: translateY(0); }
}

// ─── Doc card overlay ─────────────────────────────────────────────────────────
.rider-doc-card {
    cursor: pointer;

    &:hover .rider-doc-overlay {
        opacity: 1;
    }
}

.rider-doc-overlay {
    position: absolute;
    inset: 0;
    background: rgba(0, 0, 0, 0.4);
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    font-size: 1.4rem;
    opacity: 0;
    transition: opacity 0.2s;
}

.rider-doc-img {
    position: relative;
}

.rider-doc-hover {
    opacity: 0;
    transition: opacity 0.2s;
    background: rgba(0,0,0,0.4) !important;
}

.cursor-pointer:hover .rider-doc-hover {
    opacity: 1;
}

.border-dashed {
    border-style: dashed !important;
    opacity: 0.6;
}

// ─── Lightbox ─────────────────────────────────────────────────────────────────
.rider-doc-lightbox {
    position: fixed;
    inset: 0;
    z-index: 2000;
    background: rgba(0, 0, 0, 0.78);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 1.5rem;
}

.rider-doc-lightbox-inner {
    background: #fff;
    border-radius: 12px;
    overflow: hidden;
    max-width: 1040px;
    width: 100%;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.4);
    display: flex;
    flex-direction: column;
}

.rider-doc-lightbox-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.85rem 1.25rem;
    border-bottom: 1px solid #e9ecef;
    background: #f9fafb;
}

.rider-doc-lightbox-title {
    font-size: 0.9rem;
    font-weight: 700;
    color: #111827;
}

.rider-doc-lightbox-close {
    background: none;
    border: none;
    font-size: 1rem;
    color: #6b7280;
    cursor: pointer;
    padding: 0.25rem 0.5rem;
    border-radius: 6px;

    &:hover { background: #f3f4f6; color: #111827; }
}

.rider-doc-lightbox-body {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 1.25rem;
    background: #111;
    height: 82vh;
    max-height: 82vh;
    overflow: auto;
}

.rider-doc-lightbox-img {
    max-width: 100%;
    max-height: 75vh;
    object-fit: contain;
    border-radius: 4px;
}

.rider-doc-lightbox-frame {
    width: 100%;
    height: 100%;
    border: 0;
    border-radius: 4px;
    background: #fff;
}

// ─── Modal ────────────────────────────────────────────────────────────────────
.modal-modern .modal-dialog { max-width: 600px; }
</style>

<script lang="ts">
import { useConfirm } from '@/libs/useConfirm'
import { Role } from '@/models/Admin'
import type Rider from '@/models/Rider'
import AppNotification from '@/vue/components/AppNotification.vue'
import Block from '@/vue/components/Block.vue'
import Button from '@/vue/components/Button.vue'
import Can from '@/vue/components/Can.vue'
import CreditForm from '@/vue/components/CreditForm.vue'
import CreditPlanTable from '@/vue/components/CreditPlanTable.vue'
import CurrencyFormatter from '@/vue/components/CurrencyFormatter.vue'
import Datatable from '@/vue/components/Datatable.vue'
import EnhancedTableComponent from '@/vue/components/EnhancedTableComponent.vue'
import Form from '@/vue/components/Form.vue'
import InputField from '@/vue/components/InputField.vue'
import Modal from '@/vue/components/Modal.vue'
import MoneyField from '@/vue/components/MoneyField.vue'
import SelectField from '@/vue/components/SelectField.vue'
import TableComponent from '@/vue/components/TableComponent.vue'
import TextBlock from '@/vue/components/TextBlock.vue'
import App from '@/vue/templates/App.vue'
import { ButtonLabel } from '@/vue/utils/Button'
import { CellRender } from '@/vue/utils/Datatable'
import { Link } from '@inertiajs/vue3'
import $ from 'jquery'
import { materialTheme, Notification, NotificationProgress, Notivue, NotivueKeyboard } from 'notivue'
import { Options as Select2Options } from 'select2'
import Swal from 'sweetalert2'
import { Component, Prop, Ref, toNative, Vue } from 'vue-facing-decorator'
import SwapsaveTable from '../snippets/SwapsaveTable.vue'

@Component({
    components: {
        App,
        Block,
        TextBlock,
        Modal,
        SelectField,
        InputField,
        MoneyField,
        Button,
        Form,
        Notivue,
        Notification,
        NotificationProgress,
        NotivueKeyboard,
        AppNotification,
        Link,
        CurrencyFormatter,
        EnhancedTableComponent,
        TableComponent,
        CreditForm,
        CreditPlanTable,
        Datatable,
        SwapsaveTable,
        Can
    },
})
class Profile extends Vue {
    // State
    done = false
    message = ''
    engine_activation = ''
    loading = false
    materialTheme = materialTheme
    confirm = useConfirm().confirm

    // Section Visibility Toggles
    showCreditFormSection = false
    showActivePlansSection = false
    showAnalyticsSection = false
    showSwapSection = false
    showDocumentsSection = false
    showEngineSection = false
    showLegalSection = false

    // Document preview lightbox
    docPreview = { open: false, url: '', label: '', type: '' }

    // Update Keys for component refresh
    creditUpdateKey = 0
    repairUpdateKey = 0

    // Flags
    hasActiveCreditPlan = false
    hasActiveCreditRepair = false

    @Prop({ type: Object })
    readonly rider!: Rider

    @Prop({ type: Object })
    readonly credits!: Record<string, string>

    @Ref('ridersTable')
    readonly ridersTable!: InstanceType<typeof EnhancedTableComponent>

    // Lifecycle
    mounted() {
        this.checkActivePlans()
        this.checkActivePlans()
        this.setupActionHandlers()
    }

    get role() {
        return [Role.SUPER_ADMIN, Role.COUNTRY_MANAGER]
    }

   

    // Getters
    get label(): ButtonLabel {
        return {
            normal: 'Activate Rider',
            loading: 'activating...',
            done: 'Activated',
        }
    }

    get recordColumns() {
        return [
            { key: 'code.value', label: 'Code', type: 'text', sortable: true, searchable: false },
            { key: 'claims_made', label: 'Total Claims', type: 'text', sortable: true, searchable: false },
            { key: 'code.claim_status', label: 'Claim Status', type: 'text', sortable: true, searchable: false },
            { key: 'code.engine.serial', label: 'Engine', type: 'text', sortable: true, searchable: true },
            { key: 'created_at', label: 'Last Swap Date', type: 'date', sortable: true, searchable: false },
        ]
    }
    get creditColumns() {
        return [
            { key: 'credit_type', label: 'Type', type: 'text', sortable: true, searchable: false },
            { key: 'totalAmount.formatted', label: 'Total Amount', type: 'text', sortable: true, searchable: false },
            { key: 'repaymentPerSwap.formatted', label: 'Repayment Amount', type: 'text', sortable: true, searchable: false },
            { key: 'paidSequence', label: 'No. of Repayment Made', type: 'text', sortable: true, searchable: false },
            { key: 'balance.formatted', label: 'Remaining Balance', type: 'text', sortable: true, searchable: true },
            { key: 'created_at', label: 'Date Created', type: 'date', sortable: true, searchable: false },
        ]
    }

    get type_options(): Select2Options {
        return {
            placeholder: 'Select Engine',
            minimumResultsForSearch: 3,
            ajax: {
                method: 'get',
                url: this.route('rider.ajax.select.engines', { rider: this.rider.id }),
            },
            //@ts-ignore
            templateResult: ({ id, title, text }) => (id ? $(`<span>${title}</span>`) : text),
            //@ts-ignore
            templateSelection: ({ id, title, text }) => (id ? $(`<span>${title}</span>`) : text),
        }
    }

    // Toggle Methods
    toggleCreditFormSection() {
        this.showCreditFormSection = !this.showCreditFormSection
    }
    toggleEngineSection() {
        this.showEngineSection = !this.showEngineSection
    }

    toggleActivePlansSection() {
        this.showActivePlansSection = !this.showActivePlansSection
    }

    toggleSwapSection() {
        this.showSwapSection = !this.showSwapSection
    }

    toggleAnalyticsSection() {
        this.showAnalyticsSection = !this.showAnalyticsSection
    }

    toggleDocumentsSection() {
        this.showDocumentsSection = !this.showDocumentsSection
    }

    openDocPreview(url: string, label: string, type = '') {
        this.docPreview = { open: true, url, label, type }
    }

    toggleLegalSection() {
        this.showLegalSection = !this.showLegalSection
    }

    closeDocPreview() {
        this.docPreview = { open: false, url: '', label: '', type: '' }
    }

    documentViewerUrl(url: string, type = '') {
        if (!url) return ''

        return this.isPdfDocument(type, url) ? `${url}#toolbar=0&navpanes=0` : url
    }

    isPdfDocument(type = '', url = '') {
        const normalizedType = type.toLowerCase()
        const cleanUrl = url.split('?')[0].split('#')[0].toLowerCase()

        return normalizedType === 'application/pdf' || cleanUrl.endsWith('.pdf')
    }

    isImageDocument(type = '', url = '') {
        const normalizedType = type.toLowerCase()
        const cleanUrl = url.split('?')[0].split('#')[0].toLowerCase()

        return normalizedType.startsWith('image/') || /\.(gif|jpe?g|png|webp)$/i.test(cleanUrl)
    }

    // Modal Methods
    openModal() {
        ; (this.$refs.myModal as any).onShowModal()
    }

    closeModal() {
        ; (this.$refs.myModal as any).onHideModal()
    }

    // Credit Plan Methods
    checkActivePlans() {
        this.hasActiveCreditPlan = this.rider.credit?.activatedAt && !this.rider.credit?.completedAt
        this.hasActiveCreditRepair = this.rider.credit_repair?.activatedAt && !this.rider.credit_repair?.completedAt
    }

    handleCreditSuccess(response: any) {
        this.message = 'Credit plan successfully created!'
        Swal.fire({
            title: 'Success!',
            text: this.message,
            icon: 'success',
            confirmButtonText: 'OK',
        })
            .then(() => {
                // Refresh
                this.router.visit(this.route('rider.page.single', { rider: this.rider.id }))
            })

        this.showCreditFormSection = false
        this.checkActivePlans()
    }

    handleCreditError(payload: any) {
        console.error('Credit error:', payload.error || payload)
        this.message = payload?.message || 'An error occurred while processing the credit plan.'
        Swal.fire({
            toast: true,
            position: 'top-end',
            icon: 'error',
            title: this.message,
            showConfirmButton: false,
            timer: 4000,
            timerProgressBar: true,
        })
    }

    cancelCreditForm() {
        this.showCreditFormSection = false
    }

    cancelEngineSection() {
        this.engine_activation = ''
        this.showEngineSection = false
    }

    refreshCreditData() {
        this.creditUpdateKey++
        this.checkActivePlans()
    }

    refreshRepairData() {
        this.repairUpdateKey++
        this.checkActivePlans()
    }

    // Engine Management Methods
    async confirmAction() {
        const isConfirmed = await this.confirm('Are you sure you want to remove engine from rider?', {
            title: 'Confirm Removal',
            confirmText: 'Remove',
            cancelText: 'Keep',
        })

        if (!isConfirmed) return
        this.loading = true
        try {
            await this.unassignEngine()
        } catch (error) {
            console.error('Error:', error)
        } finally {
            this.loading = false
        }
    }

    unassignEngine() {
        this.$http
            .post(this.route('rider.ajax.unassign'), {
                engine: this.rider?.current_engine?.data[0]?.serial,
                rider: this.rider.id,
            })
            .then((response) => {
                this.done = true
                this.message = response?.data?.message
                Swal.fire({
                    title: 'Success!',
                    text: this.message,
                    icon: 'success',
                    confirmButtonText: 'OK',
                }).then(() => {
                    this.router.visit(this.route('rider.page.single', { rider: this.rider.id }))
                    this.message = ''
                    Swal.close()
                    this.closeModal()
                })
            })
            .catch((e) => {
                this.message = e.response?.data?.message || 'An error occurred.'
                console.error(e)
            })
            .finally(() => (this.loading = false))
    }

    onSubmit(payload: any) {
        this.loading = true
        payload.rider = this.rider.id
        console.log('Payload', payload)
        this.$http
            .post(this.route('rider.ajax.assign'), payload)
            .then((response) => {
                this.done = true
                this.message = response?.data?.message
                Swal.fire({
                    title: 'Success!',
                    text: this.message,
                    icon: 'success',
                    confirmButtonText: 'OK',
                }).then(() => {
                    this.router.visit(this.route('rider.page.single', { rider: this.rider.id }))
                    this.message = ''
                    Swal.close()
                    this.closeModal()
                })
            })
            .catch((e) => {
                this.message = e.response?.data?.message || 'An error occurred.'
                console.error(e)
            })
            .finally(() => (this.loading = false))
    }


    get buttons() {
        let buttons: any[] = []
        buttons.push('refresh')
        buttons.push({
            extend: 'create',
            attr: {
                href: this.route('rider.page.create'),
            },
        })
        buttons.push('trashed')

        return buttons
    }

    // Update your columnData method to accept table type parameter
    columnData(type: boolean, tableType: 'credit' | 'creditrepair') {
        let columns: any[] = []

        columns.push({
            data: 'id',
            title: 'ID',
        })

        if (type) {
            columns.push({
                data: 'type',
                title: 'Type',
                orderable: true,
                name: 'type',
            })
        }

        columns.push({
            data: 'total_amount',
            title: 'Total Amount',
            render: CellRender.money(),
        })

        columns.push({
            data: 'repayment_per_swap',
            title: 'Repayment/Swap',
            render: CellRender.money(),
        })

        columns.push({
            data: 'number_of_repayments',
            title: 'No. of Repayments',
        })

        columns.push({
            data: 'created_at',
            title: 'Activated On',
            render: CellRender.date(),
            orderable: true,
            name: 'created_at',
        })

        columns.push({
            data: 'completed_at',
            title: 'Date Completed',
            render: CellRender.date(),
            orderable: true,
            name: 'completed_at',
        })

        // Add custom actions column with table type
        columns.push({
            data: null,
            title: 'Actions',
            orderable: false,
            searchable: false,
            className: 'text-center',
            render: (data: any, type: any, row: any) => {
                const buttons: string[] = []

                // View button
                buttons.push(`
                <button
                    class="btn btn-sm bg-body-secondary text-dark me-1"
                    data-action="view"
                    data-id="${row.id}"
                    data-table-type="${tableType}"
                    title="View Details">
                    <i class="fas fa-eye"></i>
                </button>
            `)


                if (!row.completed_at) {
                    // Edit button
                    buttons.push(`
                    <button
                        class="btn btn-sm bg-body-secondary text-dark me-1"
                        data-action="edit"
                        data-id="${row.id}"
                        data-table-type="${tableType}"
                        title="Edit">
                        <i class="fas fa-edit"></i>
                    </button>
                `)

                    // Delete button
                    buttons.push(`
                    <button
                        class="btn btn-sm bg-danger text-light"
                        data-action="delete"
                        data-id="${row.id}"
                        data-table-type="${tableType}"
                        title="Delete">
                        <i class="fas fa-trash"></i>
                    </button>
                `)
                }



                return `<div class="btn-group">${buttons.join('')}</div>`
            }
        })

        return columns
    }

    // Update datatable options to pass table type
    get rider_credit_options() {
        return {
            ajax: {
                url: this.route('credit.ajax.credits', { rider: this.rider.id }),
                method: 'GET',
            },
            buttons: this.buttons,
            columns: this.columnData(false, 'credit'),
            drawCallback: () => {
                this.setupActionHandlers()
            }
        }
    }

    get rider_credit_repair_options() {
        return {
            ajax: {
                url: this.route('creditrepair.ajax.creditrepairs', { rider: this.rider.id }),
                method: 'GET',
            },
            buttons: this.buttons,
            columns: this.columnData(true, 'creditrepair'),
            drawCallback: () => {
                this.setupActionHandlers()
            }
        }
    }

    setupActionHandlers() {

        $(document).off('click', '[data-action]').on('click', '[data-action]', (e) => {
            const $button = $(e.currentTarget)
            const action = $button.data('action')
            const id = $button.data('id')
            const tableType = $button.data('table-type') as 'credit' | 'creditrepair'

            switch (action) {
                case 'view':
                    this.handleView(id, tableType)
                    break
                case 'edit':
                    this.handleEdit(id, tableType)
                    break
                case 'delete':
                    this.handleDelete(id, tableType)
                    break
                default:
                    console.warn(`Unknown action: ${action}`)
            }
        })
    }


    getRoutePrefix(tableType: 'credit' | 'creditrepair'): string {
        return tableType === 'credit' ? 'credit' : 'creditrepair'
    }


    getDisplayName(tableType: 'credit' | 'creditrepair'): string {
        return tableType === 'credit' ? 'Credit Plan' : 'Credit Repair Plan'
    }

    // Handler methods with dynamic routing
    handleView(id: number, tableType: 'credit' | 'creditrepair') {
        const routePrefix = this.getRoutePrefix(tableType)


        try {
            this.router.visit(this.route(`${routePrefix}.page.single`, { [routePrefix]: id }))
        } catch (error) {

            Swal.fire({
                title: 'View Details',
                html: `
                <p>Viewing ${this.getDisplayName(tableType)} #${id}</p>
                <p class="text-muted">Detail page not yet implemented</p>
            `,
                icon: 'info',
                confirmButtonText: 'OK',
            })
        }
    }

    handleEdit(id: number, tableType: 'credit' | 'creditrepair') {
        const routePrefix = this.getRoutePrefix(tableType)

        try {
            this.router.visit(this.route(`${routePrefix}.page.update`, { [routePrefix]: id }))
        } catch (error) {
            Swal.fire({
                title: 'Edit',
                html: `
                <p>Edit ${this.getDisplayName(tableType)} #${id}</p>
                <p class="text-muted">Edit page not yet implemented</p>
            `,
                icon: 'info',
                confirmButtonText: 'OK',
            })
        }
    }

    async handleDelete(id: number, tableType: 'credit' | 'creditrepair') {
        const displayName = this.getDisplayName(tableType)

        const isConfirmed = await this.confirm(
            `Are you sure you want to delete this ${displayName.toLowerCase()}?`,
            {
                title: 'Confirm Deletion',
                confirmText: 'Delete',
                cancelText: 'Cancel',
            }
        )

        if (!isConfirmed) return

        const routePrefix = this.getRoutePrefix(tableType)
        this.loading = true

        this.$http
            .delete(this.route(`${routePrefix}.ajax.delete`, { [routePrefix]: id }))
            .then((response) => {
                Swal.fire({
                    title: 'Success!',
                    text: `${displayName} deleted successfully`,
                    icon: 'success',
                    confirmButtonText: 'OK',
                }).then(() => {

                    this.router.visit(this.route('rider.page.single', { rider: this.rider.id }))
                })
            })
            .catch((error) => {
                Swal.fire({
                    title: 'Error!',
                    text: error.response?.data?.message || `Failed to delete ${displayName.toLowerCase()}`,
                    icon: 'error',
                    confirmButtonText: 'OK',
                })
            })
            .finally(() => {
                this.loading = false
            })
    }




    // Clean up on unmount
    beforeUnmount() {
        $(document).off('click', '[data-action]')
    }


}

export default toNative(Profile)
</script>
